import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, ExternalLink, Share2, Mail } from 'lucide-react';
import { useInView } from '../components/UI';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const team = [
  {
    name: 'Benjamin Olamuyiwa',
    role: 'Chief Executive Officer',
    tag: 'CEO',
    img: 'https://randomuser.me/api/portraits/men/32.jpg',
    bio: 'Adebayo brings 15 years of infrastructure and investment experience across Nigeria and West Africa. Previously at a leading pan-African development finance institution, he led capital deployment across energy and transport. He founded Azura on the conviction that the electric mobility transition is Africa\'s industrial inflection point.',
    linkedin: '#',
    twitter: '#',
    email: 'ceo@azuramobility.com',
  },
  {
    name: 'Emeka Nwosu',
    role: 'Head of Engineering',
    tag: 'Engineering',
    img: 'https://randomuser.me/api/portraits/men/54.jpg',
    bio: 'Emeka leads all mechanical and electrical engineering across the fleet and BaaS network. He holds an MEng in Mechanical Engineering from the University of Lagos and spent eight years designing EV drivetrain systems at a leading Asian automotive supplier before returning to Nigeria to help build the Sankofa roadmap.',
    linkedin: '#',
    twitter: '#',
    email: 'engineering@azuramobility.com',
  },
  {
    name: 'Ngozi Adeyemi',
    role: 'Head of Technology & Innovation',
    tag: 'Tech & Innovation',
    img: 'https://randomuser.me/api/portraits/women/44.jpg',
    bio: 'Ngozi leads Azura\'s technology platform: the fleet management system, BaaS operations software, AZT token infrastructure, and the data layer that ties the ecosystem together. A software engineer by training with a decade in fintech and mobility, she is building the technology stack that will scale to 500,000 vehicles.',
    linkedin: '#',
    twitter: '#',
    email: 'tech@azuramobility.com',
  },
  {
    name: 'Funmi Adesanya',
    role: 'Marketing Lead',
    tag: 'Marketing',
    img: 'https://randomuser.me/api/portraits/women/68.jpg',
    bio: 'Funmi leads brand strategy, rider acquisition, and investor communications. She has built marketing functions for three high-growth Nigerian startups and brings a deep understanding of how to build trust and reach in the Ibadan and Lagos markets. She is responsible for the Azura brand\'s voice across every channel.',
    linkedin: '#',
    twitter: '#',
    email: 'marketing@azuramobility.com',
  },
  {
    name: 'Tunde Bello',
    role: 'Head of Media',
    tag: 'Media',
    img: 'https://randomuser.me/api/portraits/men/76.jpg',
    bio: 'Tunde leads media relations, documentary production, and the storytelling that turns Azura\'s field operations into content. He spent seven years as a broadcast journalist covering infrastructure and business across Nigeria before moving into media strategy. He ensures the Sankofa story reaches the audiences that matter.',
    linkedin: '#',
    twitter: '#',
    email: 'media@azuramobility.com',
  },
];

const values = [
  { num: '01', title: 'Nigerian-first', desc: 'Every hire, every partnership, every procurement decision asks the same question: does this keep value on the continent?' },
  { num: '02', title: 'Field before forecast', desc: 'We do not model what we have not tested. Every projection in our whitepaper is grounded in real research from real streets in Ibadan.' },
  { num: '03', title: 'Long-arc thinking', desc: 'The Sankofa Foundry roadmap runs to 2045. We hire people who want to be part of a 20-year story, not a two-year sprint.' },
  { num: '04', title: 'Infrastructure mindset', desc: 'We are building things that need to last: swap stations, fleet networks, and eventually a manufacturing plant. We think like engineers, not promoters.' },
];

/* ══ TEAM PAGE ══ */
const Team: React.FC = () => {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const { ref: teamRef, inView: teamInView } = useInView();
  const { ref: valRef, inView: valInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)', backgroundSize: '70px 70px' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/30">/</span>
            <span className="text-accent-amber text-xs font-semibold">The Team</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
            Meet the Azura<br />
            <span className="text-accent-amber">Mobility Team</span>
          </h1>

          <p className="max-w-2xl text-white/70 text-base leading-8">
            Azura is built by Africans who understand this market from the inside. Every member of the founding team was chosen for deep, specific expertise in the area they lead
          </p>
        </div>
      </section>

      {/* ══ TEAM GRID ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={teamRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${teamInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-bg-dark/40">Founding Team</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              Five leaders. <span className="text-accent-amber italic">One mission.</span>
            </h2>
          </div>

          {/* CEO — featured full-width card */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="mb-6">
            <motion.div variants={fadeUp}
              className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl overflow-hidden border border-border shadow-dark">

              {/* image */}
              <div className="lg:col-span-4 relative min-h-[320px] bg-neutral-100">
                <img src={team[0].img} alt={team[0].name}
                  className="w-full h-full object-cover object-center absolute inset-0"
                  onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-bg-dark/20" />
                {/* amber blob */}
                <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-accent-amber opacity-20 blur-2xl pointer-events-none" />
              </div>

              {/* content */}
              <div className="lg:col-span-8 bg-bg-dark p-10 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                <span className="absolute bottom-5 right-8 font-figtree font-black text-[6rem] leading-none text-white/[0.03] select-none pointer-events-none">CEO</span>

                <div className="mb-5">
                  <span className="inline-block font-mono text-[9px] font-bold uppercase tracking-widest text-accent-amber/60 border border-accent-amber/20 bg-accent-amber/10 px-3 py-1 rounded-full mb-3">{team[0].tag}</span>
                  <h3 className="font-figtree font-black text-3xl text-white mb-1">{team[0].name}</h3>
                  <p className="font-mono text-[10px] text-white/35 uppercase tracking-widest">{team[0].role}</p>
                </div>

                <p className="font-exo text-sm text-white/55 leading-relaxed mb-7 max-w-xl">{team[0].bio}</p>

                <div className="flex items-center gap-3">
                  <a href={team[0].linkedin}
                    className="w-9 h-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:bg-accent-amber hover:text-bg-dark hover:border-accent-amber transition-all duration-200">
                    <ExternalLink size={14} />
                  </a>
                  <a href={team[0].twitter}
                    className="w-9 h-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:bg-accent-amber hover:text-bg-dark hover:border-accent-amber transition-all duration-200">
                    <Share2 size={14} />
                  </a>
                  <a href={`mailto:${team[0].email}`}
                    className="inline-flex items-center gap-2 ml-2 font-mono text-[9px] text-white/30 hover:text-accent-amber transition-colors uppercase tracking-widest">
                    <Mail size={12} /> {team[0].email}
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* remaining 4 team members */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.slice(1).map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className={`group rounded-2xl overflow-hidden border shadow-card hover:shadow-dark transition-all duration-300 cursor-pointer ${activeIdx === i + 1 ? 'border-bg-dark' : 'border-border'}`}
                onClick={() => setActiveIdx(activeIdx === i + 1 ? null : i + 1)}>

                {/* photo */}
                <div className="relative aspect-[3/3.5] bg-neutral-100 overflow-hidden">
                  <img src={member.img} alt={member.name}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
                    onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 via-bg-dark/10 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block font-mono text-[8px] font-bold uppercase tracking-widest text-bg-dark bg-accent-amber px-2.5 py-1 rounded-full mb-2">{member.tag}</span>
                    <p className="font-figtree font-black text-white text-base leading-tight">{member.name}</p>
                    <p className="font-mono text-[9px] text-white/50 mt-0.5 uppercase tracking-widest">{member.role}</p>
                  </div>
                </div>

                {/* bio — expands on click */}
                <div className={`bg-white overflow-hidden transition-all duration-400 ${activeIdx === i + 1 ? 'max-h-80 p-6' : 'max-h-0'}`}>
                  {activeIdx === i + 1 && (
                    <>
                      <p className="font-exo text-xs text-neutral-600 leading-relaxed mb-5">{member.bio}</p>
                      <div className="flex items-center gap-2 pt-4 border-t border-border">
                        <a href={member.linkedin}
                          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-neutral-400 hover:bg-bg-dark hover:text-accent-amber hover:border-bg-dark transition-all duration-200">
                          <ExternalLink size={13} />
                        </a>
                        <a href={member.twitter}
                          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-neutral-400 hover:bg-bg-dark hover:text-accent-amber hover:border-bg-dark transition-all duration-200">
                          <Share2 size={13} />
                        </a>
                        <a href={`mailto:${member.email}`}
                          className="font-mono text-[8px] text-neutral-400 hover:text-bg-dark transition-colors uppercase tracking-widest ml-1">
                          {member.email}
                        </a>
                      </div>
                    </>
                  )}
                </div>
                {activeIdx !== i + 1 && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-border">
                    <span className="font-bold text-xs text-neutral-700">View bio</span>
                    <span className="text-neutral-700 text-xs">+</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHAT WE LOOK FOR ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white border-t-4 border-accent-amber relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div ref={valRef} className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className={`lg:col-span-5 transition-all duration-700 ${valInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">What We Look For</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5">
                The Azura team<br />
                <span className="text-accent-amber italic">is still being built.</span>
              </h2>
              <p className="font-exo text-white/55 text-sm leading-relaxed mb-4">
                As Azura moves from founding phase to fleet launch, from fleet to BaaS, and from BaaS to Sankofa, the team will grow at every tier. We hire people who want to be part of something that lasts — who understand that building infrastructure on a continent is a 20-year commitment, not a two-year sprint.
              </p>
              <p className="font-exo text-white/40 text-sm leading-relaxed mb-8">
                If you have deep expertise in EV engineering, battery systems, fleet operations, urban logistics, or technology — and you believe Africa should build its own vehicles — we would like to hear from you.
              </p>
              <a href="mailto:team@azuramobility.com"
                className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-6 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
                Get in Touch <ArrowRight size={13} />
              </a>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((v, i) => (
                <div key={v.num}
                  className={`bg-white/[0.04] border border-white/[0.08] rounded-2xl p-7 relative overflow-hidden transition-all duration-700 hover:bg-white/[0.07] ${valInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}>
                  <span className="absolute top-4 right-5 font-figtree font-black text-4xl leading-none text-white/[0.05] select-none">{v.num}</span>
                  <div className="w-8 h-8 rounded-full bg-accent-amber/10 border border-accent-amber/20 flex items-center justify-center mb-5">
                    <div className="w-2 h-2 rounded-full bg-accent-amber" />
                  </div>
                  <h3 className="font-figtree font-bold text-sm text-white mb-2 leading-snug">{v.title}</h3>
                  <p className="font-exo text-xs text-white/45 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ JOIN THE TEAM CTA ══ */}
      <section className="py-14 px-6 bg-bg-cream border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-figtree font-black text-2xl text-bg-dark mb-1">Interested in joining Azura?</p>
            <p className="font-exo text-sm text-neutral-500">We are actively hiring for engineering, operations, and technology roles ahead of Phase 1 launch.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a href="mailto:team@azuramobility.com"
              className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
              Send your CV <ArrowRight size={13} />
            </a>
            <Link to="/about"
              className="inline-flex items-center gap-2 border border-bg-dark/30 text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-bg-dark transition-all">
              About Azura ↗
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Team;